import { create } from 'zustand';
import i18n from '../i18n';
import { clientBusinessApi } from '../api/clientBusinessApi';
import { clientProvidersApi } from '../api/clientProvidersApi';
import type { Business, ClientProvider, ConfirmationModel } from '../types/client';

interface ClientTenantState {
  business: Business | null;
  providers: ClientProvider[];
  providerCount: number;
  confirmationModel: ConfirmationModel | null;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  fetchBootstrap: () => Promise<void>;
  /**
   * Tilni almashtirgandan keyin chaqirish — idempotent guard'ni o'tkazib
   * yuborib backend'dan yangi Accept-Language bilan ma'lumotni qaytarib oladi.
   */
  refresh: () => Promise<void>;
}

async function loadTenantBootstrap(): Promise<{
  business: Business;
  providers: ClientProvider[];
  confirmationModel: ConfirmationModel | null;
}> {
  const [business, providers] = await Promise.all([
    clientBusinessApi.get(),
    clientProvidersApi.getList(),
  ]);

  // confirmationModel: barcha providerlar bir xil bookingMode bo'lsa set, aks holda null
  const modes = new Set(providers.map((p) => p.defaultBookingMode));
  const confirmationModel: ConfirmationModel | null =
    modes.size === 1
      ? modes.has('FREE_AUTO_CONFIRM')
        ? 'INSTANT'
        : 'APPROVAL_REQUIRED'
      : null;

  return { business, providers, confirmationModel };
}

export const useClientTenantStore = create<ClientTenantState>((set, get) => ({
  business: null,
  providers: [],
  providerCount: 0,
  confirmationModel: null,
  isLoading: false,
  isFetching: false,
  error: null,

  fetchBootstrap: async () => {
    // Idempotent guard — bir marta yuklash
    if (get().isFetching) return;
    if (get().business !== null) return;

    set({ isLoading: true, isFetching: true, error: null });

    try {
      const next = await loadTenantBootstrap();
      set({
        business: next.business,
        providers: next.providers,
        providerCount: next.providers.length,
        confirmationModel: next.confirmationModel,
        isLoading: false,
        isFetching: false,
      });
    } catch {
      set({ error: "Ma'lumotlarni yuklashda xatolik", isLoading: false, isFetching: false });
    }
  },

  refresh: async () => {
    // Guard fetchBootstrap bilan birga ishlamasin
    if (get().isFetching) return;
    // business yo'q bo'lsa fetchBootstrap o'zi mas'ul — refresh chaqirmaymiz
    if (get().business === null) return;

    set({ isFetching: true, error: null });

    try {
      const next = await loadTenantBootstrap();
      set({
        business: next.business,
        providers: next.providers,
        providerCount: next.providers.length,
        confirmationModel: next.confirmationModel,
        isFetching: false,
      });
    } catch {
      set({ isFetching: false });
    }
  },
}));

// Til o'zgargach business + providers backenddan Accept-Language yangi
// qiymati bilan qaytarib o'qiladi. Modul darajasi singleton — komponent
// unmount bo'lganda yo'qolmaydi (foydalanuvchi Profile tabida til
// o'zgartirib Home'ga qaytsa allaqachon yangi data tayyor).
if (typeof window !== 'undefined') {
  i18n.on('languageChanged', () => {
    const state = useClientTenantStore.getState();
    // business yo'q bo'lsa fetchBootstrap o'zi keyingi mount paytida ishlaydi
    if (state.business !== null) {
      void state.refresh();
    }
  });
}
